import { gridSize, useDebounce } from '@/shared';
import { Theme } from '@emotion/react';
import { Autocomplete, Grid, SxProps, TextField } from '@mui/material';
import { Control, Controller, FieldError } from 'react-hook-form';
import './CustomAutocomplete.css';
import { useEffect, useState } from 'react';
import { ApiResponse } from '@/shared/interfaces/erp/global/response-axios.interface';

export type CustomDynamicSearchProps<T> = {
  name: string;
  loadingText?: string;
  label: string;
  disabled?: boolean;

  valueKey: keyof T;
  actualValueKey: keyof T;
  optionLabelForEdit?: string;
  onChangeValue?: (value: any) => void;
  onChangeRawValue?: (value: T) => void;

  paramsToGetData?:Partial<T>;
  functionToGetData: (value?: Partial<T> & {  
    page?: number;
    page_size?: number;
  }) => Promise<ApiResponse<{
    [key:string]: any;
    data: T[];
  }>>;

  error: FieldError | undefined;
  helperText: React.ReactNode;
  required?: boolean;

  textFieldKey?: string;
  defaultValue: string | number | null | undefined;
  control: Control<any, any>;

  size?: {
    xs: number;
    sm: number;
    md: number;
    lg: number;
  };
  disableClearable?: boolean;

  sxTextField?: SxProps<Theme>;
  endAdornmentInput?: React.ReactNode;

  //send with enter
  enableToSubmitWithEnter?: boolean;
  handleSubmit?: () => void;

};

function CustomDynamicSearch<T>({
  name,
  error,
  textFieldKey,
  defaultValue,
  control,
  loadingText = 'Cargando...',
  optionLabelForEdit,
  valueKey,
  helperText,
  required = true,
  label,
  actualValueKey,
  onChangeValue,
  onChangeRawValue,

  paramsToGetData,
  functionToGetData,

  size = gridSize,
  disabled = false,
  disableClearable = false,
  endAdornmentInput,

  //send with enter
  enableToSubmitWithEnter = false,
  handleSubmit = () => console.log("nothing happen"),
}: CustomDynamicSearchProps<T>) {
  const [searchingWord, setSearchingWord] = useState("");
  const [fetchedOptions, setFetchedOptions] = useState<T[]|[]>();
  const debouncedSearchingWord = useDebounce(searchingWord,600)

  
  
  useEffect(() => {
    
    async function getDataOnFetch() {
      const params = {
        ...paramsToGetData,
        [valueKey]: debouncedSearchingWord,
        page_size: 5,
      } as Partial<T> & { page_size?: number; page?: number };
      const dataOptions = await functionToGetData(params)
      setFetchedOptions(dataOptions?.data?.data)
    }
    if(searchingWord && searchingWord?.length > 2){
      getDataOnFetch()
    }

  }, [debouncedSearchingWord]);

  useEffect(()=>{
    async function getDataOnFetch() {
      const params = {
        ...paramsToGetData,
        [actualValueKey ?? valueKey]: defaultValue,
        page_size: 5,
      } as Partial<T> & { page_size?: number; page?: number };
      const dataOptions = await functionToGetData(params)
      setFetchedOptions(dataOptions?.data?.data)
    }
    getDataOnFetch()
  },[])


  return (
    <Grid item {...size}>
      <Controller

        name={name}
        control={control}
        key={textFieldKey ?? defaultValue ?? 'default'}
        defaultValue={defaultValue ?? ''}
        render={({ field }) => {
          const onChange = (_event: any, data: any) => {
            const selectedValue = data?.[actualValueKey ?? valueKey] ?? '';
            field.onChange(selectedValue);
            onChangeValue && onChangeValue(selectedValue); // label
            onChangeRawValue && onChangeRawValue(data); // T
          };

          const onKeyDown = (_event: any) => {
            enableToSubmitWithEnter ?
              _event.key === 'Enter' ? (
                // console.log('Enter key pressed')
                handleSubmit()
              )
                : null
              : null
          }

          return (
            <Autocomplete
              {...field}
              popupIcon={null}
              disabled={disabled}
              //freeSolo
              noOptionsText={'No hay opciones'}
              loading={ (fetchedOptions && fetchedOptions?.length>0) || false}
              loadingText={loadingText}
              options={fetchedOptions && fetchedOptions?.length>0 ?fetchedOptions:[]}
              disableClearable={disableClearable}
              getOptionLabel={(option: any) =>
                option?.[valueKey]?.toString() ??
                fetchedOptions?.find(opt => opt[actualValueKey ?? valueKey] === option)
                  ?.[valueKey]?.toString() ??
                optionLabelForEdit?.toString() ??
                defaultValue ?? ''
              }
              renderInput={params => (
                <TextField
                  {...params}
                  InputProps={{
                    ...params.InputProps,
                    ...(endAdornmentInput && {
                      endAdornment: endAdornmentInput,
                    }),
                  }}
                  autoComplete='off'
                  label={label}
                  variant="outlined"
                  error={!!error}
                  helperText={helperText}
                  required={required}
                  sx={{
                    '& .css-1i3ux3q-MuiInputBase-input-MuiOutlinedInput-input.Mui-disabled':
                    {
                      opacity: 0.8,
                      WebkitTextFillColor: 'black',
                    },
                  }}
                  onChange={(e)=>setSearchingWord(e.target.value)}
                />
              )}
              onChange={onChange}
              onKeyDown={onKeyDown}
            />
          );
        }}
      />
    </Grid>
  );
}

export default CustomDynamicSearch;